import { memo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { QuestionnaireFormRenderer } from "src-next/shared/ui/questionnaire-form-renderer";
import type { QuestionPreviewItem } from "src-next/shared/ui/questionnaire-form-renderer";

export interface PreviewExpandDialogProps {
  open: boolean;
  onClose: () => void;
  questions: QuestionPreviewItem[];
  title?: string;
  description?: string;
  emptyMessage: string;
  emptyOptionsMessage?: string;
}

/**
 * Dialog de preview expandido do questionário.
 *
 * Simula o layout da tela de resposta: largura controlada (~640px), centralizada,
 * mobile-first. Em telas pequenas (xs, sm), abre em fullScreen para melhor UX.
 */
const PreviewExpandDialog = memo(
  ({
    open,
    onClose,
    questions,
    title,
    description,
    emptyMessage,
    emptyOptionsMessage,
  }: PreviewExpandDialogProps) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isFullScreen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview do questionário</DialogTitle>
        <DialogContent
          dividers
          sx={{ p: { xs: 2, sm: 3 } }}
        >
          <QuestionnaireFormRenderer
            questions={questions}
            title={title}
            description={description}
            emptyMessage={emptyMessage}
            emptyOptionsMessage={emptyOptionsMessage}
            maxWidth={640}
            sx={{ border: "none", p: 0, backgroundColor: "transparent" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button onClick={onClose} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

PreviewExpandDialog.displayName = "PreviewExpandDialog";

export default PreviewExpandDialog;
